/**
 * Action Cable: espera estado terminal de um AgentRun (substitui polling no chat).
 * Fallback: um GET ao expirar o tempo ou se a subscrição for recusada.
 */
import { createConsumer } from '@rails/actioncable'

function buildCableUrl() {
  const userToken = localStorage.getItem('userToken')
  const tenantToken = localStorage.getItem('tenantToken')
  if (!userToken || !tenantToken) return null

  const { protocol, host } = window.location
  const wsProto = protocol === 'https:' ? 'wss:' : 'ws:'
  const base = `${wsProto}//${host}/cable`
  return `${base}?user_token=${encodeURIComponent(userToken)}&tenant_token=${encodeURIComponent(tenantToken)}`
}

async function fetchRunOnce(get, runId) {
  const { data, error } = await get(`/api/v1/agent_runs/${runId}?include_steps=false`)
  if (error || !data) return null
  if (['succeeded', 'failed', 'cancelled'].includes(data.status)) return data
  return null
}

function normalizeFromCablePayload(ar) {
  if (!ar) return null
  return {
    id: ar.id,
    status: ar.status,
    output_payload: ar.output_payload,
    error_message: ar.error_message
  }
}

/**
 * @param {number|string} runId
 * @param {{ get: Function, timeoutMs?: number }} options
 * @returns {Promise<object|null>} objeto no formato do GET agent_runs/show ou null
 */
export function waitForTerminalAgentRun(runId, { get, timeoutMs = 120000 }) {
  const id = Number(runId)
  if (!Number.isFinite(id) || id < 1) return Promise.resolve(null)

  return (async () => {
    const alreadyDone = await fetchRunOnce(get, id)
    if (alreadyDone) return alreadyDone

    const url = buildCableUrl()
    if (!url) {
      return fetchRunOnce(get, id)
    }

    return new Promise((resolve) => {
      let settled = false
      const consumer = createConsumer(url)
      let subscription = null

      const finish = (payload) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        try {
          subscription?.unsubscribe()
        } catch (e) {
          /* ignore */
        }
        try {
          consumer.disconnect()
        } catch (e) {
          /* ignore */
        }
        resolve(payload)
      }

      const timer = setTimeout(async () => {
        const fallback = await fetchRunOnce(get, id)
        finish(fallback)
      }, timeoutMs)

      subscription = consumer.subscriptions.create(
        { channel: 'AgentRunChannel', agent_run_id: id },
        {
          rejected() {
            fetchRunOnce(get, id).then((r) => finish(r))
          },
          received(data) {
            if (!data || data.event !== 'terminal' || !data.agent_run) return
            const normalized = normalizeFromCablePayload(data.agent_run)
            if (normalized && ['succeeded', 'failed', 'cancelled'].includes(normalized.status)) {
              finish(normalized)
            }
          }
        }
      )
    })
  })()
}
